import React, { forwardRef, useState, useEffect, useMemo, useRef } from 'react';
import { View, Heading, Text, colors, BasicChip } from '@fairygodboss/ui';
import ResponsivePicture from '../../../Elements/ResponsivePicture/ResponsivePicture';
import { labelToTextMap } from '../adviceHashMap';
import { EmitMetric } from '../../../Analytics/VisibilityPixel/VisibilityPixel';
import { useAdvicePage } from '../AdvicePageContext';

export const EditorialContentCard = forwardRef(
    ({ content, carouselView, index }, ref) => {
        const {
            currentSlide,
            impressionMetricMap,
            onFireImpression,
        } = useAdvicePage();
        const [fireImpression, setFireImpression] = useState(false);

        const currentSlideRef = useRef(currentSlide);

        useEffect(() => {
            currentSlideRef.current = currentSlide;
        }, [currentSlide]);

        useEffect(() => {
            if (typeof currentSlide !== 'number') {
                return;
            }

            const cardSlide = Math.ceil((index + 1) / 6);
            const shouldFire = cardSlide === currentSlideRef.current;

            setFireImpression(shouldFire);
        }, [index, currentSlide, content]);

        const {
            commentCount,
            likes,
            contentUrl,
            title,
            contentImage,
            topic,
            hash,
        } = content;

        const impressionMetrics = useMemo(
            () => ({
                misc_event_type: 'advice-hub-impression',
                community_content_key: hash,
                community_content_location: 'advice-content',
                impression: 1,
                // TODO: if we have sponsored company, add company ID
                company_id: [null],
                snowPlow: {
                    component: 'tile',
                    tileType: 'article',
                    position: 'main',
                    pageType: 'collection',
                    pageSection: 'advice_landing',
                },
            }),
            [hash]
        );

        useEffect(() => {
            if (fireImpression && !impressionMetricMap[hash]) {
                onFireImpression({ hash });
                EmitMetric(impressionMetrics);
            }
        }, [
            fireImpression,
            impressionMetricMap,
            hash,
            impressionMetrics,
            onFireImpression,
            currentSlide,
        ]);

        const label = topic?.label;
        const commentText = commentCount === 1 ? 'Comment' : 'Comments';
        const likeText = likes === 1 ? 'Like' : 'Likes';

        const [rerenderKey, setRerenderKey] = useState(0);

        const [height, setHeight] = useState(
            carouselView?.height ? `${carouselView?.height}px` : null
        );
        const [width, setWidth] = useState(
            carouselView?.width ? `${carouselView?.width}px` : null
        );
        const [imageHeight, setImageHeight] = useState(
            carouselView?.imageHeight ? `${carouselView?.imageHeight}px` : null
        );

        useEffect(() => {
            const randomNumber = Math.floor(Math.random() * 1001);
            setRerenderKey(randomNumber);
        }, [imageHeight]);

        useEffect(() => {
            const _width = carouselView?.width
                ? `${carouselView?.width}px`
                : null;

            if (_width !== width) {
                setWidth(
                    carouselView?.width ? `${carouselView?.width}px` : null
                );
                setHeight(
                    carouselView?.height ? `${carouselView?.height}px` : null
                );

                setImageHeight(
                    carouselView?.imageHeight
                        ? `${carouselView?.imageHeight}px`
                        : null
                );
            }
        }, [carouselView, width]);

        const emitClickMetric = () => {
            EmitMetric({
                click: 1,
                community_content_key: hash,
                // TODO: if we have sponsored company, add company ID
                company_id: [null],
            });
        };

        return (
            <View position="relative" height={height} width={width}>
                <View
                    className="editorial-content-card"
                    borderRadius="8px"
                    as="a"
                    href={contentUrl}
                    target="_self"
                    onClick={emitClickMetric}
                >
                    <View
                        ref={ref}
                        css={{
                            ':hover': {
                                boxShadow:
                                    '0px 24px 40px rgba(53, 53, 53, 0.16)',
                            },
                        }}
                        borderRadius="8px"
                        backgroundColor="#FFFFFF"
                    >
                        <View
                            height={imageHeight}
                            width={width}
                            maxHeight={imageHeight}
                            maxWidth={width}
                            overflow="hidden"
                            borderTopRightRadius="8px"
                            borderTopLeftRadius="8px"
                            key={rerenderKey}
                        >
                            <ResponsivePicture
                                cloudinaryID={contentImage?.src}
                                alt={contentImage?.alt}
                                lazyLoad
                            />
                        </View>

                        <View
                            borderRadius="0 0 8px 8px"
                            border={`1px solid ${colors.neutral[20]}`}
                            px="16px"
                            py="16px"
                            color={colors.neutral[80]}
                            css={{
                                span: {
                                    ':hover': {
                                        textDecoration: 'underline',
                                        color: colors.neutral[100],
                                    },
                                },
                            }}
                        >
                            <Heading
                                variant={{
                                    _: 'headingXs',
                                    md: 'headingSm',
                                }}
                                color={colors.neutral[100]}
                                height={{
                                    _: 'auto',
                                    sm: '72px',
                                    md: '120px',
                                    lg: '90px',
                                }}
                                css={{
                                    overflow: 'hidden',
                                    display: '-webkit-box',
                                    '-webkit-line-clamp': '3',
                                    '-webkit-box-orient': 'vertical',
                                }}
                            >
                                {title}
                            </Heading>
                            <Text
                                pt="8px"
                                as="span"
                                margin="0"
                                textAlign="left"
                                variant="labelMd"
                            >
                                {`${likes} ${likeText}`}
                            </Text>
                            {'   •  '}
                            <Text
                                pt="8px"
                                as="span"
                                margin="0"
                                textAlign="left"
                                variant="labelMd"
                            >
                                {`${commentCount} ${commentText}`}
                            </Text>
                        </View>
                    </View>
                </View>
                <View
                    css={{
                        span: {
                            borderWidth: '0px',
                            backgroundColor: colors.secondary[10],
                            color: colors.secondary[70],
                            fontWeight: '600',
                        },
                    }}
                >
                    <BasicChip
                        boxShadow="0px 2px 4px rgba(0, 0, 0, 0.16)"
                        position="absolute"
                        left="16px"
                        top="16px"
                        size="medium"
                        text={labelToTextMap[label]}
                        variant="primary"
                    />
                </View>
            </View>
        );
    }
);
